import React from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  makeStyles,
  Typography
} from "@material-ui/core";
import { AsyncDropdown } from "src/apps/commons/dropdowns";
import {
  listAllRemyndBrands,
  listAllRemyndCatLv1s,
  listAllRemyndCatLv2s,
  listAllRemyndCatLv3s
} from "src/apps/commons/dropdowns/api";
import { IRemyndWarrantyBrandMapSaveReqTemp } from "../types";

const useStyles = makeStyles(_ => ({
  container: {
    "& > *": {
      padding: "12px 6px"
    }
  }
}));

interface Props {
  label: string;
  open: boolean;
  defaultValue?: IRemyndWarrantyBrandMapSaveReqTemp;
  existingValues?: IRemyndWarrantyBrandMapSaveReqTemp[];
  handleCloseModal: () => void;
  handleSubmitModal: (value: IRemyndWarrantyBrandMapSaveReqTemp) => void;
}

const schema = yup.object().shape({
  brand: yup.object().typeError("กรุณาเลือกแบรนด์สินค้า"),
  categoryLv1: yup.object().nullable(),
  categoryLv2: yup.object().nullable(),
  categoryLv3: yup.object().nullable()
});

const WarrantyBrandCatModal: React.FC<Props> = props => {
  const classes = useStyles();
  const {
    open,
    label,
    defaultValue,
    handleCloseModal,
    handleSubmitModal,
    existingValues
  } = props;

  const {
    handleSubmit,
    control,
    formState: { errors },
    setError,
    setValue,
    watch
  } = useForm<PartialNull<IRemyndWarrantyBrandMapSaveReqTemp>>({
    defaultValues: { ...defaultValue },
    resolver: yupResolver(schema)
  });

  const handleCheckBeforeSubmit = (
    form: IRemyndWarrantyBrandMapSaveReqTemp
  ) => {
    const dup = existingValues?.some(
      c =>
        (c.categoryLv3?.value ?? "") === (form.categoryLv3?.value ?? "") &&
        (c.categoryLv2?.value ?? "") === (form.categoryLv2?.value ?? "") &&
        (c.categoryLv1?.value ?? "") === (form.categoryLv1?.value ?? "") &&
        (c.brand?.value ?? "") === (form.brand?.value ?? "")
    );
    if (dup) {
      const errorField = form.categoryLv3
        ? "categoryLv3"
        : form.categoryLv2
        ? "categoryLv2"
        : form.categoryLv1
        ? "categoryLv1"
        : "brand";
      setError(errorField, {
        message:
          errorField === "brand"
            ? "แบรนด์ ซ้ำกับที่มีเลือกไปแล้ว"
            : "แบรนด์ และ หมวดหมู่ ซ้ำกับที่มีเลือกไปแล้ว"
      });
      return;
    }

    handleSubmitModal(form);
  };

  const categoryLv1 = watch("categoryLv1");
  const categoryLv2 = watch("categoryLv2");

  return (
    <Dialog
      open={open}
      onClose={handleCloseModal}
      maxWidth="md"
      fullWidth
      aria-labelledby="form-dialog-title"
    >
      <form onSubmit={handleSubmit(handleCheckBeforeSubmit)}>
        <DialogTitle id="form-dialog-title" disableTypography>
          <Typography variant="h3">
            {!defaultValue ? "เพิ่ม " : "แก้ไข "}
            {label}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Grid container className={classes.container}>
            <Grid item xs={12}>
              <Controller
                name="brand"
                control={control}
                defaultValue={defaultValue?.brand ?? null}
                render={({ field: { onChange, value } }) => (
                  <AsyncDropdown
                    label="แบรนด์สินค้า"
                    fetchDropdown={listAllRemyndBrands}
                    paginate={{
                      page: 1,
                      pageSize: 2000,
                      order: {
                        optionKey: "NAME_TH",
                        desc: false
                      }
                    }}
                    onChange={onChange}
                    value={value}
                    error={Boolean(errors.brand)}
                    helperText={errors.brand?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="categoryLv1"
                control={control}
                defaultValue={defaultValue?.categoryLv1 ?? null}
                render={({ field: { onChange, value } }) => (
                  <AsyncDropdown
                    label="หมวดสินค้า Level 1"
                    fetchDropdown={listAllRemyndCatLv1s}
                    paginate={{
                      page: 1,
                      pageSize: 2000,
                      order: {
                        optionKey: "NAME_TH",
                        desc: false
                      }
                    }}
                    onChange={v => {
                      onChange(v);
                      setValue("categoryLv3", null);
                      setValue("categoryLv2", null);
                    }}
                    value={value}
                    error={Boolean(errors.categoryLv1)}
                    helperText={errors.categoryLv1?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="categoryLv2"
                control={control}
                defaultValue={defaultValue?.categoryLv2 ?? null}
                render={({ field: { onChange, value } }) => (
                  <AsyncDropdown
                    disabled={!categoryLv1}
                    label="หมวดสินค้า Level 2"
                    fetchDropdown={listAllRemyndCatLv2s}
                    paginate={{
                      page: 1,
                      pageSize: 2000,
                      order: {
                        optionKey: "NAME_TH",
                        desc: false
                      }
                    }}
                    additionalReqParams={{
                      search: { lv1Id: categoryLv1?.value }
                    }}
                    onChange={v => {
                      onChange(v);
                      setValue("categoryLv3", null);
                    }}
                    value={value}
                    error={Boolean(errors.categoryLv2)}
                    helperText={errors.categoryLv2?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="categoryLv3"
                control={control}
                defaultValue={defaultValue?.categoryLv3 ?? null}
                render={({ field: { onChange, value } }) => (
                  <AsyncDropdown
                    disabled={!categoryLv2}
                    label="หมวดสินค้า Level 3"
                    fetchDropdown={listAllRemyndCatLv3s}
                    paginate={{
                      page: 1,
                      pageSize: 2000,
                      order: {
                        optionKey: "NAME_TH",
                        desc: false
                      }
                    }}
                    additionalReqParams={{
                      search: { lv2Id: categoryLv2?.value }
                    }}
                    onChange={onChange}
                    value={value}
                    error={Boolean(errors.categoryLv3)}
                    helperText={errors.categoryLv3?.message}
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="primary">
            Cancel
          </Button>
          {!defaultValue ? (
            <Button
              key="addBtn"
              variant="contained"
              color="primary"
              type="submit"
            >
              Add
            </Button>
          ) : (
            <Button
              key="updateBtn"
              variant="contained"
              color="primary"
              type="submit"
            >
              Update
            </Button>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default WarrantyBrandCatModal;
